import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import ImageViewer from 'components/field/ImageViewer';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import OrderStatuses from 'pages/Shipments/ActionForms/OrderStatuses';

import { ORDERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const ViewReturnDetails = NiceModal.create(({ ID }) => {
	const axios = useAxiosPrivate();
	const formMethods = useForm();
	const { reset, watch } = formMethods;

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const { showError } = useAlert();
	const fetchOrderDetails = async () => {
		setLoading(true);

		await axios
			.post(ORDERS.GET_RETURNED_ORDER_DETAILS, { ID })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						orderID: data?.orderID,
						returnType: data?.returnType,
						refundAmount: data?.refundedAmount,
						returnReason: data?.returnReason,
						returnReasonOptions: data?.returnReasonOptions,
						photos: data?.photos
					});
					setData(data?.items);
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setLoading(false);
	};

	useEffect(() => {
		ID && fetchOrderDetails();
	}, [ID]);

	const colSpan = useMemo(() => ({ md: 6 }), []);

	const columns = useMemo(
		() => [
			{
				header: 'productName',
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				size: 150,
				showOnMobile: true
			},
			{
				header: 'sku',
				accessorKey: 'sku',
				size: 100,
				showOnMobile: true
			},
			{
				header: 'orderedQuantity',
				accessorKey: 'qtyOrdered',
				size: 100
			},
			{
				header: 'qtyReturnRequested',
				accessorKey: 'quantityReturnRequested',
				size: 100
			},
			{
				header: 'qtyConfirmedReturn',
				accessorKey: 'qtyTobeReturned',
				size: 100
			},
			{
				header: 'returnedQuantity',
				accessorKey: 'qtyReturned',
				size: 100
			},
			{
				header: 'returnReason',
				accessorKey: 'itemReturnReason',
				size: 200
			}
		],
		[data]
	);

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-viewReturnDetails">
			<ModalTitle>
				<Typography variant="p1">viewReturnDetails</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderID" colSpan={colSpan} isLoading={loading}>
						<StringInput name="orderID" disabled />
					</FormItem>
					<FormItem label="returnType" colSpan={colSpan} isLoading={loading}>
						<StringInput name="returnType" disabled />
					</FormItem>
					<FormItem label="refundAmount" colSpan={colSpan} isLoading={loading}>
						<StringInput name="refundAmount" disabled />
					</FormItem>
					<FormItem label="returnReasonOptions" colSpan={colSpan} isLoading={loading}>
						<StringInput name="returnReasonOptions" disabled />
					</FormItem>
					<FormItem label="returnComment" isLoading={loading}>
						<StringInput name="returnReason" disabled />
					</FormItem>
					<FormItem>
						<Table data={data} columns={columns} loading={loading} />
					</FormItem>
					<FormItem label="returnReasonAttachments" isLoading={loading}>
						{watch('photos')?.map((photo, i) => (
							<ImageViewer key={i} imageUrl={photo} />
						))}
					</FormItem>
					<FormItem>
						<OrderStatuses ID={ID} />
					</FormItem>
				</Form>
			</ModalContent>
		</Modal>
	);
});

export default ViewReturnDetails;
